<svelte:options runes={false} />

<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, InputV2, Text } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { capFirstChar, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import SettingGuide from '../SettingGuide.svelte';

	export let device: Device;

	const offsetMin = -50;
	const offsetMax = 50;
	const thresholdMin = 1;
	const thresholdMax = 20;
	const settings = readDeviceSettings(real_id(device.id));
	const humiditySettings = settings?.['humidity:0'];
	const initialOffset = Number(humiditySettings?.offset ?? 0);
	const initialThreshold = Number(humiditySettings?.report_thr ?? thresholdMin);

	let offsetValue = String(initialOffset);
	let thresholdValue = String(initialThreshold);
	let savedOffset = initialOffset;
	let savedThreshold = initialThreshold;
	let loading = false;

	$: deviceFwVersion =
		(
			settings?.DeviceInfo?.ver ||
			settings?.sys?.device?.fw_id ||
			settings?.DeviceInfo?.fw_id
		)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0] || '0.0.1';
	$: supportsOffset = compareFWVersions(deviceFwVersion, '>=', '0.11.4');
	$: numericOffset =
		offsetValue === '' || offsetValue === null || offsetValue === undefined
			? NaN
			: Number(offsetValue);
	$: numericThreshold =
		thresholdValue === '' || thresholdValue === null || thresholdValue === undefined
			? NaN
			: Number(thresholdValue);
	$: offsetInputData = getErrorOrInfoMessageForMinAndMax(
		numericOffset,
		offsetMin,
		offsetMax,
	);
	$: thresholdInputData = getErrorOrInfoMessageForMinAndMax(
		numericThreshold,
		thresholdMin,
		thresholdMax,
	);
	$: offsetDirty = supportsOffset && numericOffset !== savedOffset;
	$: thresholdDirty = numericThreshold !== savedThreshold;
	$: isDirty = offsetDirty || thresholdDirty;
	$: hasError =
		(supportsOffset && !!offsetInputData.error) || !!thresholdInputData.error;
	$: saveDisabled = loading || !isDirty || hasError;

	const save = async () => {
		if (saveDisabled) return;

		const offsetToSave = numericOffset;
		const thresholdToSave = numericThreshold;
		const saveOffset = offsetDirty;
		const saveThreshold = thresholdDirty;
		const config: Record<string, number> = {};
		if (saveOffset) config.offset = offsetToSave;
		if (saveThreshold) config.report_thr = thresholdToSave;

		loading = true;
		const res: SettingRequest = await gen2Request(device, 'Humidity.SetConfig', {
			id: device.channel,
			config,
		});
		if (res?.isok) {
			if (saveOffset) savedOffset = offsetToSave;
			if (saveThreshold) savedThreshold = thresholdToSave;
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}
</script>

<div class="humidity-settings">
	<SettingGuide
		text={$format('settings_guide_humidity_sensor', {
			default: 'Calibrate humidity readings and choose when humidity changes are reported.',
		})}
	/>

	{#if supportsOffset}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{capFirstChar(
					$format('sub_settings_title_humidity_offset', {
						default: 'humidity offset',
					}),
				)}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('text_sensor_settings_humidity', {
					default: 'Humidity offset in %. Value is applied to measured humidity.',
				})}
			</Text>
			<InputV2
				{...getNumberInputConfig(true)}
				id="input-humidity-settings-offset"
				aria-label={$format('input_humidity_offset', {
					default: 'Enter the humidity offset. Value must be between {min} — {max}!',
					values: { min: offsetMin, max: offsetMax },
				})}
				icon={null}
				size="m"
				width="full"
				min={offsetMin}
				max={offsetMax}
				step={1}
				bind:value={offsetValue}
				info={offsetInputData.info}
				error={offsetInputData.error}
			/>
		</Container>
	{/if}

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{$format('sub_settings_title_humidity_threshold', {
				default: 'Humidity threshold',
			})}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('humidity_threshold_description', {
				default:
					'Threshold for triggering a device wake up. Relative Humidity change with more than the specified value triggers the device to report sensor data.',
			})}
		</Text>
		<InputV2
			{...getNumberInputConfig(true)}
			id="input-humidity-settings-threshold"
			aria-label={$format('sub_settings_title_humidity_threshold', {
				default: 'Humidity threshold',
			})}
			icon={null}
			size="m"
			width="full"
			min={thresholdMin}
			max={thresholdMax}
			step={1}
			bind:value={thresholdValue}
			info={thresholdInputData.info}
			error={thresholdInputData.error}
		/>
	</Container>
</div>

<style>
	.humidity-settings {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
